/**
 * Operations API client.
 *
 * Auth token comes from POST /api/pp/auth/user-pin { pin, section: "operations_edit" }
 * and is kept in sessionStorage.
 */
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const TOKEN_KEY = "ops_edit_token";

export const getToken = () => {
  try { return sessionStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
};
export const setToken = (t) => {
  try {
    if (t) sessionStorage.setItem(TOKEN_KEY, t);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch { /* noop */ }
};
export const clearToken = () => setToken("");
const headers = () => ({ "X-Auth-Token": getToken() });

// ─── Auth ────────────────────────────────────────────────────────────────
export const loginWithPin = async (pin) => {
  const { data } = await axios.post(`${API}/pp/auth/user-pin`, {
    pin,
    section: "operations_edit",
  });
  if (data.valid && data.token) {
    setToken(data.token);
    return { ok: true, user: data.user };
  }
  return { ok: false, reason: data.reason || "invalid" };
};

// ─── Config ──────────────────────────────────────────────────────────────
export const getConfig = () =>
  axios.get(`${API}/operations/config`, { headers: headers() }).then((r) => r.data);

// ─── Statements ──────────────────────────────────────────────────────────
export const uploadStatements = (files, onProgress, company = "", statementType = "") => {
  const fd = new FormData();
  for (const f of files) fd.append("files", f);
  return axios
    .post(`${API}/operations/statements/upload`, fd, {
      headers: {
        ...headers(),
        ...(company ? { "X-Company": company } : {}),
        ...(statementType ? { "X-Statement-Type": statementType } : {}),
      },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
      timeout: 5 * 60 * 1000, // 5 min for large PDFs + LLM call
    })
    .then((r) => r.data);
};

export const listCompanies = () =>
  axios.get(`${API}/operations/companies`, { headers: headers() }).then((r) => r.data);

export const getStatementStatus = (id) =>
  axios.get(`${API}/operations/statements/${id}/status`, { headers: headers() }).then((r) => r.data);

export const listStatements = () =>
  axios.get(`${API}/operations/statements`, { headers: headers() }).then((r) => r.data);

export const deleteStatement = (id, confirmPin) =>
  axios
    .delete(`${API}/operations/statements/${id}`, {
      headers: { ...headers(), "X-Confirm-Pin": confirmPin },
    })
    .then((r) => r.data);

// ─── Transactions ────────────────────────────────────────────────────────
export const listTransactions = (params = {}) =>
  axios
    .get(`${API}/operations/transactions`, { headers: headers(), params })
    .then((r) => r.data);

export const updateTransaction = (id, patch) =>
  axios.put(`${API}/operations/transactions/${id}`, patch, { headers: headers() }).then((r) => r.data);

export const createManualTransaction = (payload) =>
  axios.post(`${API}/operations/transactions`, payload, { headers: headers() }).then((r) => r.data);

export const deleteTransaction = (id) =>
  axios.delete(`${API}/operations/transactions/${id}`, { headers: headers() }).then((r) => r.data);

// ─── Dashboard ───────────────────────────────────────────────────────────
export const getDashboard = (costCentre, source) => {
  const params = {};
  if (costCentre) params.cost_centre = costCentre;
  if (source) params.source = source;
  return axios
    .get(`${API}/operations/dashboard`, { headers: headers(), params })
    .then((r) => r.data);
};

// ─── Budgets ─────────────────────────────────────────────────────────────
export const setBudget = ({ cost_centre, category, month, amount }) =>
  axios
    .put(
      `${API}/operations/budgets`,
      { cost_centre, category, month, amount },
      { headers: headers() },
    )
    .then((r) => r.data);

// ─── Loans summary (reimbursement tracker) ───────────────────────────────
export const getLoansSummary = ({ company } = {}) =>
  axios
    .get(`${API}/operations/loans/summary`, {
      params: company ? { company } : {},
      headers: headers(),
    })
    .then((r) => r.data);

// ─── Formatting helpers ──────────────────────────────────────────────────
export const money = (n) =>
  Math.abs(Number(n) || 0).toLocaleString("en-CA", {
    style: "currency",
    currency: "CAD",
  });
